import type { Realtime, RealtimeEvent, RealtimeRow } from "../src/realtime-types";

/**
 * The Realtime screen: visitors in the last 5 minutes, a per-minute chart of the last 30, the
 * pages people are on, and a live event feed. Polls /api/realtime every 10 s while shown.
 * Self-contained (own styles, own timer) so app.ts only has to call start/stop.
 */

export interface RealtimeHost {
  api: <T>(path: string, p?: Record<string, string>) => Promise<T>;
  projectId: string;
  mobile: boolean;
  /** False once the user has moved to another screen or project. */
  current: () => boolean;
  /** Shown above the content on mobile (project switcher). */
  mobileHead?: string;
  errorCard: (err: unknown) => string;
}

const POLL_MS = 10_000;
let timer: ReturnType<typeof setTimeout> | null = null;
let ticker: ReturnType<typeof setInterval> | null = null;
let lastData: Realtime | null = null;
let lastAt = 0;
/** Feed rows already on screen, so new ones can be highlighted as they arrive. */
let seen = new Set<string>();

let onVisible: (() => void) | null = null;

export function stopRealtime(): void {
  if (timer) clearTimeout(timer);
  if (ticker) clearInterval(ticker);
  timer = ticker = null;
  if (onVisible) document.removeEventListener("visibilitychange", onVisible);
  onVisible = null;
}

export async function startRealtime(root: HTMLElement, host: RealtimeHost): Promise<void> {
  stopRealtime();
  injectStyles();
  seen = new Set();
  lastData = null;
  root.innerHTML = `${host.mobileHead ?? ""}<div class="rt">${skeletonLayout()}</div>`;

  let first = true;
  const tick = async () => {
    if (!host.current()) return stopRealtime();
    // Hidden tabs don't poll, but always load once so a tab opened in the background isn't blank.
    if (first || !document.hidden) {
      first = false;
      try {
        const data = await host.api<Realtime>("realtime", { project: host.projectId });
        if (!host.current()) return;
        lastAt = Date.now();
        draw(root, host, data);
        // Only after a successful draw, so a drawing bug shows the error instead of a stuck skeleton.
        lastData = data;
      } catch (err) {
        console.error("realtime", err);
        if (!host.current()) return;
        // Keep the last good data on screen; only show the error if there is nothing yet.
        if (!lastData) root.querySelector(".rt")!.innerHTML = host.errorCard(err);
        else setStatus(root, "Couldn't refresh — retrying");
      }
    }
    if (timer) clearTimeout(timer);
    timer = setTimeout(tick, POLL_MS);
  };
  onVisible = () => {
    if (!document.hidden && host.current()) void tick();
  };
  document.addEventListener("visibilitychange", onVisible);
  ticker = setInterval(() => lastData && setStatus(root, `Updated ${ago(lastAt)}`), 1000);
  await tick();
}

// ---------- drawing ----------

function draw(root: HTMLElement, host: RealtimeHost, d: Realtime): void {
  const el = root.querySelector<HTMLElement>(".rt");
  if (!el) return;
  if (!d.available) {
    el.innerHTML = `<div class="notice">Realtime reads the live event store, which isn't connected here (no Analytics Engine token). It works on the deployed dashboard.</div>`;
    return;
  }
  const fresh = new Set<string>();
  const feed = d.feed.map((e) => {
    const id = `${e.ts}|${e.visitor}|${e.type}|${e.name}`;
    const isNew = seen.size > 0 && !seen.has(id);
    fresh.add(id);
    return feedRow(e, isNew);
  });
  seen = fresh;

  const hero = `<section class="card rt-hero">
    <div class="rt-now">
      <span class="rt-dot"><i></i><i></i></span>
      <div><div class="rt-big num">${fmtN(d.visitors)}</div><div class="rt-sub">${d.visitors === 1 ? "visitor" : "visitors"} in the last 5 minutes</div></div>
      <span class="spacer"></span>
      <div class="rt-mini"><div><b class="num">${fmtN(d.views)}</b><span>views</span></div><div><b class="num">${fmtN(d.events)}</b><span>events</span></div></div>
    </div>
    ${minuteBars(d.minutes)}
    <div class="rt-status" aria-live="off">Updated ${ago(lastAt)}</div>
  </section>`;

  const pages = card("Current pages", rows(d.pages, (r) => `<span class="rt-path" title="${esc((r.host ?? "") + r.key)}">${esc(r.key)}</span>`, true), "Visitors");
  const feedCard = card("Live feed", feed.length ? `<ol class="rt-feed">${feed.join("")}</ol>` : empty("No events in the last 30 minutes."), "Last 30 min");
  const channels = card("Channels", rows(d.channels, (r) => esc(r.key)), "Visitors");
  const countries = card("Countries", rows(d.countries, (r) => `<span class="iso">${esc(r.key)}</span> ${esc(countryName(r.key))}`), "Visitors");
  const devices = card("Devices", rows(d.devices, (r) => esc(cap(r.key))), "Visitors");

  const scrollTop = el.querySelector(".rt-feed")?.scrollTop ?? 0;
  el.innerHTML = host.mobile
    ? `${hero}${pages}${feedCard}${countries}${channels}${devices}`
    : `${hero}<div class="row2">${pages}${feedCard}</div><div class="rt-row3">${channels}${countries}${devices}</div>`;
  const f = el.querySelector(".rt-feed");
  if (f) f.scrollTop = scrollTop;
}

function setStatus(root: HTMLElement, text: string): void {
  const s = root.querySelector(".rt-status");
  if (s) s.textContent = text;
}

function minuteBars(minutes: Realtime["minutes"]): string {
  const max = Math.max(1, ...minutes.map((m) => m.visitors));
  const bars = minutes
    .map((m, i) => {
      const h = m.visitors ? Math.max(6, Math.round((m.visitors / max) * 100)) : 0;
      const t = utcToLocalTime(m.minute);
      const recent = i >= minutes.length - 5;
      return `<div class="rt-bar${recent ? " recent" : ""}" title="${t}: ${m.visitors} ${m.visitors === 1 ? "visitor" : "visitors"}, ${m.views} views"><i style="height:${h}%"></i></div>`;
    })
    .join("");
  const first = minutes[0] ? utcToLocalTime(minutes[0].minute) : "";
  return `<div class="rt-bars" role="img" aria-label="Visitors per minute, last 30 minutes">${bars}</div>
    <div class="rt-axis"><span>${first}</span><span>15 min ago</span><span>Now</span></div>`;
}

const TYPE_LABEL: Record<string, string> = { pageview: "Viewed", screen: "Opened", event: "Event", error: "Error", identify: "Signed in" };

function feedRow(e: RealtimeEvent, isNew: boolean): string {
  const what =
    e.type === "pageview" || e.type === "screen"
      ? `<span class="rt-path">${esc(e.type === "screen" ? e.name : e.path || e.name)}</span>`
      : `<b>${esc(e.name)}</b>${e.path ? ` <span class="rt-on">on ${esc(e.path)}</span>` : ""}`;
  const where = [e.city || countryName(e.country), [cap(e.device), e.browser].filter(Boolean).join(" · ")].filter((s) => s && s !== "Unknown").join(" — ");
  const plat = e.platform && e.platform !== "web" ? `<span class="rt-plat">${esc(e.platform)}</span>` : "";
  return `<li class="${isNew ? "new" : ""}">
    <span class="rt-who" style="background:${tagColour(e.visitor)}" title="Visitor ${esc(e.visitor)}"></span>
    <div class="rt-what"><span class="rt-type ${esc(e.type)}">${TYPE_LABEL[e.type] ?? esc(e.type)}</span>${what}${plat}
      <div class="rt-where">${esc(where)}</div></div>
    <time class="rt-time" datetime="${new Date(e.ts).toISOString()}">${ago(e.ts)}</time>
  </li>`;
}

function rows(list: RealtimeRow[], name: (r: RealtimeRow) => string, warm = false): string {
  if (!list.length) return empty("Nobody right now.");
  const max = Math.max(1, list[0]!.visitors);
  return `<div class="rt-rows">${list
    .map(
      (r) => `<div class="rt-r"><div class="rt-fill${warm ? " warm" : ""}" style="width:${(r.visitors / max) * 100}%"></div>
      <span class="k">${name(r)}</span><span class="v num">${fmtN(r.visitors)}</span></div>`,
    )
    .join("")}</div>`;
}

const card = (title: string, body: string, hint = "") =>
  `<section class="card"><div class="card-head"><h2>${title}</h2><span class="spacer"></span>${hint ? `<span class="rt-hint">${hint}</span>` : ""}</div>${body}</section>`;
const empty = (text: string) => `<div class="rt-empty">${text}</div>`;

function skeletonLayout(): string {
  const sk = (h: number) => `<div class="skeleton" style="height:${h}px"></div>`;
  return `<section class="card">${sk(170)}</section><div class="row2"><section class="card">${sk(300)}</section><section class="card">${sk(300)}</section></div>`;
}

// ---------- formatting ----------

const fmtN = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : Math.round(n).toLocaleString("en-US"));
const esc = (s: string) => String(s ?? "").replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);
const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();
function countryName(code: string): string {
  if (!code) return "Unknown";
  try {
    return regionNames?.of(code) ?? code;
  } catch {
    return code;
  }
}

function ago(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.floor(m / 60)}h ago`;
}

/** "2026-10-09 15:30:00" (UTC, from Analytics Engine) as local "15:30" / "7:30 PM". */
function utcToLocalTime(s: string): string {
  const d = new Date(`${s.replace(" ", "T")}Z`);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** A steady colour per visitor tag, from a palette that reads on the card background. */
const PALETTE = ["#2f5bd3", "#2f9e63", "#e0a43a", "#d2553c", "#7a4fd0", "#1b8fa6", "#c2477e", "#5e6880"];
function tagColour(tag: string): string {
  let h = 0;
  for (const c of tag) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

// ---------- styles (kept here so the screen is one file) ----------

let styled = false;
function injectStyles(): void {
  if (styled) return;
  styled = true;
  const css = `
.rt { display: flex; flex-direction: column; gap: 20px; }
.rt-hero { display: flex; flex-direction: column; gap: 16px; }
.rt-now { display: flex; align-items: center; gap: 16px; }
.rt-dot { position: relative; width: 14px; height: 14px; flex: none; }
.rt-dot i { position: absolute; inset: 0; border-radius: 50%; background: var(--good); }
.rt-dot i:first-child { animation: mbpulse 1.6s ease-out infinite; }
.rt-big { font-size: 44px; font-weight: 600; letter-spacing: -.02em; line-height: 1; }
.rt-sub { color: var(--text-2); font-size: 14px; margin-top: 4px; }
.rt-mini { display: flex; gap: 24px; }
.rt-mini div { display: flex; flex-direction: column; align-items: flex-end; }
.rt-mini b { font-size: 20px; font-weight: 600; }
.rt-mini span { font-size: 12px; color: var(--muted); }
.rt-bars { display: grid; grid-template-columns: repeat(30, 1fr); gap: 3px; height: 72px; align-items: end; }
.rt-bar { height: 100%; display: flex; align-items: flex-end; border-radius: 3px; background: var(--row-divider); }
.rt-bar i { display: block; width: 100%; background: var(--blue-light); border-radius: 3px; }
.rt-bar.recent i { background: var(--blue); }
.rt-axis { display: flex; justify-content: space-between; font-family: var(--mono); font-size: 10px; color: var(--muted); margin-top: -8px; }
.rt-status { font-size: 12px; color: var(--muted); }
.rt-hint { font-family: var(--mono); font-size: 10px; letter-spacing: .06em; text-transform: uppercase; color: var(--muted); }
.rt-row3 { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr)); gap: 20px; }
.rt-rows { display: flex; flex-direction: column; gap: 4px; }
.rt-r { position: relative; display: flex; align-items: center; gap: 12px; height: 36px; padding: 0 10px; font-size: 14px; }
.rt-fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 8px; background: var(--blue-tint); }
.rt-fill.warm { background: var(--warm-tint); }
.rt-r .k { position: relative; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: flex; gap: 8px; align-items: center; }
.rt-r .v { position: relative; font-weight: 600; font-variant-numeric: tabular-nums; }
.rt-path { font-family: var(--mono); font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rt-empty { color: var(--muted); font-size: 14px; padding: 12px 0; }
.rt-feed { list-style: none; margin: 0; padding: 0; max-height: 420px; overflow-y: auto; }
.rt-feed li { display: flex; gap: 12px; align-items: flex-start; padding: 10px 0; border-top: 1px solid var(--row-divider); }
.rt-feed li:first-child { border-top: 0; }
.rt-feed li.new { animation: rtflash 2.4s ease-out; }
@keyframes rtflash { from { background: var(--live-bg); } to { background: transparent; } }
.rt-who { width: 10px; height: 10px; border-radius: 50%; flex: none; margin-top: 5px; }
.rt-what { flex: 1; min-width: 0; font-size: 14px; display: flex; flex-wrap: wrap; gap: 4px 8px; align-items: baseline; }
.rt-what .rt-path { max-width: 100%; }
.rt-type { font-size: 11px; font-weight: 600; padding: 2px 6px; border-radius: 6px; background: var(--track); color: var(--text-2); }
.rt-type.event, .rt-type.identify { background: var(--blue-tint); color: var(--blue); }
.rt-type.error { background: var(--bad-bg); color: var(--bad-ink); }
.rt-on { color: var(--text-2); font-size: 13px; }
.rt-plat { font-family: var(--mono); font-size: 10px; text-transform: uppercase; color: var(--muted); }
.rt-where { flex-basis: 100%; font-size: 12px; color: var(--muted); }
.rt-time { font-size: 12px; color: var(--muted); white-space: nowrap; font-variant-numeric: tabular-nums; }
@media (max-width: 640px) {
  .rt { gap: 12px; }
  .rt-big { font-size: 36px; }
  .rt-mini { gap: 16px; }
  .rt-bars { height: 56px; gap: 2px; }
  .rt-feed { max-height: none; }
}
@media (prefers-reduced-motion: reduce) { .rt-dot i:first-child, .rt-feed li.new { animation: none; } }
`;
  const style = document.createElement("style");
  style.textContent = css;
  document.head.append(style);
}
